import { useState, type FormEvent } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import { Header } from "../components/shell";
import { Dialog } from "../components/overlay";
import { ErrorState, Notice, PageHeader, SkeletonBlock } from "../components/ui";
import type { Dossier } from "../api/types";
import { useCurrentUser } from "../auth";
import { formatDateTime, w } from "../i18n";

/** Opens the most recent dossier, or lets the user create the first one. */
export function WorkspaceHome() {
  const [params] = useSearchParams();
  const dossiers = useResource("dossiers", api.listDossiers);
  const deleted = useResource("dossiers:deleted", api.listDeletedDossiers);
  const [removing, setRemoving] = useState<Dossier | null>(null);
  const restore = useMutation(api.restoreDossier);
  const { can } = useCurrentUser();
  const approver = can("approver");
  const picking = params.get("pick") === "1" || params.get("new") === "1";
  if (!picking && dossiers.data?.length) return <Navigate to={`/dossiers/${dossiers.data[0].id}`} replace />;

  return <div className="ws-app">
    <Header />
    <main className="ws-main" id="ws-main"><div className="ws-main-inner" style={{ maxWidth: 720 }}>
      {dossiers.loading && <SkeletonBlock lines={6} />}
      {dossiers.error && <ErrorState title={w("common.genericError")} error={dossiers.error} onRetry={dossiers.reload} />}
      {dossiers.data && <>
        <PageHeader title={w("home.title")} lead={w("home.lead")} />
        <CreateDossier />
        {dossiers.data.length > 0 && <section className="ws-card" style={{ marginTop: 24 }}>
          <div className="ws-card-head"><h2>{w("home.select")}</h2></div>
          <ul className="ws-list">{dossiers.data.map(dossier => <li key={dossier.id}>
            <span className="ws-list-title"><Link className="ws-link" to={`/dossiers/${dossier.id}`}><strong>{dossier.name}</strong></Link>
              <span className="ws-cell-sub">{dossier.entity_name} · {w("app.reportingYear", { year: dossier.reporting_year })}</span></span>
            <span className="ws-row"><span className="ws-small ws-faint">{formatDateTime(dossier.created_at)}</span>
              <button className="ws-btn sm ghost" onClick={() => setRemoving(dossier)} disabled={!approver}
                title={approver ? w("home.delete") : w("auth.needs", { role: w("role.user.approver") })}
                aria-label={`${w("home.delete")} ${dossier.name} · ${dossier.entity_name} · ${dossier.reporting_year}`}><Trash2 size={14} /></button></span>
          </li>)}</ul></section>}
        {(deleted.data?.length ?? 0) > 0 && <section className="ws-card" style={{ marginTop: 24 }}>
          <div className="ws-card-head"><h2>{w("home.deletedTitle")}</h2>
            <span className="ws-small ws-muted">{w("home.deletedHint")}</span></div>
          {restore.error && <div className="ws-card-pad"><ErrorState title={w("common.genericError")} error={restore.error} /></div>}
          <ul className="ws-list">{deleted.data!.map(dossier => <li key={dossier.id}>
            <span className="ws-list-title"><strong className="ws-muted">{dossier.name}</strong>
              <span className="ws-cell-sub">{dossier.entity_name} · {w("app.reportingYear", { year: dossier.reporting_year })}
                {dossier.deleted_at && ` · ${w("home.deletedOn", { date: formatDateTime(dossier.deleted_at), actor: dossier.deleted_by || "" })}`}</span></span>
            <button className="ws-btn sm" disabled={!approver || restore.pending}
              onClick={async () => { if (await restore.run(dossier.id)) invalidate("dossiers"); }}>
              <RotateCcw size={14} />{w("home.restore")}</button>
          </li>)}</ul></section>}
      </>}
      {removing && <DeleteDossier dossier={removing} onClose={() => setRemoving(null)} />}
    </div></main>
  </div>;
}

/** Soft delete, confirmed by typing the dossier's name: its content is kept and can be restored. */
function DeleteDossier({ dossier, onClose }: { dossier: Dossier; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const remove = useMutation(api.deleteDossier);
  const submit = async () => {
    if (await remove.run(dossier.id, typed.trim())) { invalidate("dossiers"); onClose(); }
  };
  return <Dialog title={w("home.deleteTitle", { name: `${dossier.name} · ${dossier.entity_name}` })} onClose={onClose} size="sm"
    footer={<><button className="ws-btn" onClick={onClose} data-autofocus>{w("common.cancel")}</button>
      <button className="ws-btn danger" disabled={remove.pending || typed.trim() !== dossier.name} onClick={submit}>
        <Trash2 size={14} />{w("home.delete")}</button></>}>
    <Notice tone="caution">{w("home.deleteBody", { entity: dossier.entity_name, year: dossier.reporting_year })}</Notice>
    <div className="ws-field" style={{ marginTop: 12 }}>
      <label htmlFor="del-name">{w("home.deleteConfirm", { name: dossier.name })}</label>
      <input id="del-name" className="ws-input" value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" /></div>
    {remove.error && <ErrorState title={w("common.genericError")} error={remove.error} />}
  </Dialog>;
}

function CreateDossier() {
  const navigate = useNavigate();
  const year = new Date().getFullYear() - 1;
  const [form, setForm] = useState({ name: `SFCR ${year}`, entity_name: "", reporting_year: String(year), report_type: "SFCR" });
  const create = useMutation(api.createDossier);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const dossier = await create.run({ ...form, reporting_year: Number(form.reporting_year) });
    if (dossier) { invalidate("dossiers"); navigate(`/dossiers/${dossier.id}/documents`); }
  };
  const field = (key: keyof typeof form) => ({ value: form[key], onChange: (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value }) });
  return <form className="ws-card ws-card-pad" onSubmit={submit}>
    <div className="ws-grid-2">
      <div className="ws-field"><label htmlFor="d-entity">{w("home.entity")}</label><input id="d-entity" className="ws-input" required maxLength={200} {...field("entity_name")} /></div>
      <div className="ws-field"><label htmlFor="d-name">{w("home.name")}</label><input id="d-name" className="ws-input" required maxLength={200} {...field("name")} /></div>
      <div className="ws-field"><label htmlFor="d-year">{w("home.year")}</label><input id="d-year" className="ws-input" required type="number" min={2000} max={2100} {...field("reporting_year")} /></div>
      <div className="ws-field"><label htmlFor="d-type">{w("home.type")}</label>
        <select id="d-type" className="ws-select" {...field("report_type")}><option value="SFCR">SFCR</option><option value="RSR">RSR</option></select></div>
    </div>
    {create.error && <ErrorState title={w("common.genericError")} error={create.error} />}
    <div className="ws-row" style={{ justifyContent: "flex-end", marginTop: 8 }}>
      <button className="ws-btn primary" type="submit" disabled={create.pending}>{w("home.create")}</button></div>
  </form>;
}
